import React, { FC, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { Footer } from '@components/molecules/Footer';
import { StickyHeader } from '@components/molecules/StickyHeader/StickyHeader';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import sidebarStyles from './MyAccountSidebar.module.css';
import styles from './MyAccountContainer.module.css';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { Modules, useModule } from '@hooks/useModule';

type MyAccountContainerSubComponents = {
    Filters: typeof Filters;
};

interface MyAccountContainerProps {
    title: string;
    overrideOnDesktop?: JSX.Element;
    children: JSX.Element | JSX.Element[];
}

export const MyAccountContainer: FC<MyAccountContainerProps> & MyAccountContainerSubComponents = ({
    title,
    overrideOnDesktop,
    children,
}: MyAccountContainerProps) => {
    const { navigate } = useCustomNavigate();
    const { login } = useData();
    const { t } = useTranslation();

    const translations = {
        sidebarTitle: t('my-account-sidebar.title'),
    };

    // if user is not logged in, he can't access My Account area
    useEffect(() => {
        if (login === null) {
            navigate(`/${SITE_URLS.login}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    return (
        <>
            <StickyHeader />

            <div className="position-relative" data-testid="my-account-container">
                <Container classes="px-3 mb-5 pt-5 py-lg-5">
                    <div className="d-flex gap-4">
                        <MyAccountSidebar />
                        <div className="my-account-content w-100 flex-grow-1">
                            {/* open sidebar */}
                            <Typography classes="d-lg-none mb-3">
                                <a
                                    className="text-decoration-none text-dark"
                                    data-bs-toggle="collapse"
                                    href="#collapseMerchantSidebar"
                                    role="button"
                                    aria-expanded="false"
                                    aria-controls="collapseMerchantSidebar">
                                    <i className="fa-light fa-arrow-left me-2" /> {translations.sidebarTitle}
                                </a>
                            </Typography>
                            {!overrideOnDesktop ? (
                                <Typography
                                    appearanceLevel={TypographyAppearanceVariants.level6}
                                    tagVariant={TypographyTagVariants.h1}>
                                    {title}
                                </Typography>
                            ) : (
                                overrideOnDesktop
                            )}

                            {children}
                        </div>
                    </div>
                </Container>
            </div>

            <Footer mode="normal" />
        </>
    );
};

interface FiltersProps {
    children: JSX.Element;
}

const Filters: FC<FiltersProps> = ({ children }) => {
    return (
        <div className={styles.filters} data-testid="my-account-filters">
            {children}
        </div>
    );
};

MyAccountContainer.Filters = Filters;

const MyAccountSidebar = () => {
    const { pathname } = useLocation();
    const { login } = useData();
    const { can } = usePermissionChecker();
    const { isModuleActive } = useModule();
    const { t } = useTranslation();

    const translations = {
        greeting: t('my-account-sidebar.greeting', { userName: login?.data?.user?.name || '' }),
        title: t('my-account-sidebar.title'),
        dashboard: t('dashboard-component.title'),
        myProfile: t('my-profile-component.title'),
        myOrders: t('my-orders-component.title'),
        registerCompany: t('register-company-component.title'),
        myCompanies: t('my-companies-component.title'),
        manageProducts: t('manage-products-component.title'),
        manageServices: t('manage-services-component.title'),
        manageOrders: t('manage-orders-component.title'),
        manageAddresses: t('manage-addresses-component.title'),
        myCoupons: t('my-coupons-component.title'),
        myPoints: t('my-points-component.title'),
        promotions: t('promotions-component.title'),
        adhereToPromos: t('adhere-to-promos-component.title'),
        statistics: t('statistics-component.title'),
    };

    const canAccessMerchantAreas = can(UserPermissions.accessMerchantAreas);

    const sidebarLinks = [
        // canAccessMerchantAreas && {
        //     title: translations.dashboard,
        //     icon: 'fa-light fa-gauge',
        //     link: SITE_URLS.myDashboard,
        // },
        { title: translations.myProfile, icon: 'fa-light fa-user-hair', link: SITE_URLS.myProfile },
        { title: translations.myOrders, icon: 'fa-light fa-boxes-stacked', link: SITE_URLS.myOrders },
        canAccessMerchantAreas && {
            title: translations.myCompanies,
            icon: 'fa-light fa-suitcase',
            link: SITE_URLS.myCompanies,
        },
        canAccessMerchantAreas && {
            title: translations.manageProducts,
            icon: 'fa-light fa-tag',
            link: SITE_URLS.myProducts,
        },
        canAccessMerchantAreas && {
            title: translations.manageServices,
            icon: 'fa-light fa-briefcase',
            link: SITE_URLS.myServices,
        },
        canAccessMerchantAreas && {
            title: translations.manageOrders,
            icon: 'fa-light fa-boxes-stacked',
            link: SITE_URLS.manageOrders,
        },
        { title: translations.manageAddresses, icon: 'fa-light fa-house', link: SITE_URLS.myAddresses },
        canAccessMerchantAreas &&
            isModuleActive(Modules.coupon) && {
                title: translations.myCoupons,
                icon: 'fa-light fa-ticket',
                link: SITE_URLS.myCoupons,
            },
        // canAccessMerchantAreas && {
        //     title: translations.promotions,
        //     icon: 'fa-light fa-megaphone',
        //     link: '', // #TODO: add link
        // },
        canAccessMerchantAreas && {
            title: translations.adhereToPromos,
            icon: 'fa-light fa-plus',
            link: SITE_URLS.myAdherences,
        },
        // canAccessMerchantAreas && {
        //     title: translations.statistics,
        //     icon: 'fa-light fa-chart-line',
        //     link: '', // #TODO: add link
        // },
        isModuleActive(Modules.pointsGamification) && {
            title: translations.myPoints,
            icon: 'fa-light fa-gift',
            link: SITE_URLS.myPoints,
        },
        !canAccessMerchantAreas && {
            title: translations.registerCompany,
            icon: 'fa-light fa-suitcase',
            link: SITE_URLS.myCompaniesCreate,
        },
    ].filter(Boolean) as { title: string; icon: string; link: string }[];

    return (
        <div
            id="collapseMerchantSidebar"
            className={`${sidebarStyles['my-account-sidebar']} collapse collapse-horizontal sticky-top`}>
            <div
                className={`${sidebarStyles['sidebar-content']} d-lg-flex flex-column h-100 my-lg-0 me-lg-1 py-5 py-lg-0 pe-lg-4`}>
                <div className="container p-lg-0">
                    <Typography classes="d-lg-none mb-3" appearanceLevel={TypographyAppearanceVariants.level2}>
                        {translations.greeting}
                    </Typography>
                    <Typography classes="text-decoration-none" appearanceLevel={TypographyAppearanceVariants.level7}>
                        {translations.title}
                    </Typography>
                    <hr className="mb-2" />
                    <ul className="nav flex-column mb-auto">
                        {sidebarLinks.map((item, index) => {
                            // remove initial '/' from pathname
                            const isActive = pathname.substring(1) === item.link;

                            return (
                                <li key={`menu-item-${index}`}>
                                    <Anchor
                                        href={item.link}
                                        classes={`nav-link ${sidebarStyles['nav-link']} ${isActive && item.link !== '' ? sidebarStyles.active : ''}`}
                                        mode={AnchorMode.none}>
                                        <>
                                            <i className={item.icon} /> {item.title}
                                        </>
                                    </Anchor>
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <div className="d-none d-lg-block sidebar-vertical-separator"></div>
            </div>
        </div>
    );
};
